import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProjectsListTable, type ProjectsListColumn } from '../components/ProjectsListTable';
import { useColumnOrder } from '../hooks/useColumnOrder';
import { customerApi } from '../services/api';
import type { InvitationDetails } from '../types';

const DEFAULT_COLUMN_ORDER = ['customerName', 'customerEmail', 'customerPhone', 'status', 'createdAt'];

const FILTER_STATUSES = ['pending', 'sent', 'accepted', 'rejected', 'revoked'] as const;

/** Same colours as the other invitation lists. */
const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-warning text-dark',
  sent: 'bg-info',
  accepted: 'bg-success',
  rejected: 'bg-danger',
  revoked: 'bg-secondary'
};

const nameOf = (invitation: InvitationDetails) => invitation.customerName || invitation.contactName || '';
const emailOf = (invitation: InvitationDetails) => invitation.customerEmail || invitation.contactEmail || '';
const phoneOf = (invitation: InvitationDetails) => invitation.customerPhone || invitation.contactPhone || '';
const isActiveInvitation = (status: string) => ['pending', 'sent'].includes(status);

/**
 * The contractor's client invitations, in the same list table as the projects
 * and the client list (ClickUp 869fj439m): search, status filter, sorting and
 * paging, with resend and the new link per invitation.
 */
export function CustomerInvitationStatus() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<InvitationDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [registrationUrls, setRegistrationUrls] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState('');
  const [columnOrder, handleColumnReorder] = useColumnOrder('customerInvitationListColumns', DEFAULT_COLUMN_ORDER);

  useEffect(() => {
    customerApi.getCustomerInvitations()
      .then(setInvitations)
      .catch(error => {
        console.error('Failed to load customer invitations:', error);
        setLoadFailed(true);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleResend = async (invitation: InvitationDetails) => {
    setResendingId(invitation.id);
    setActionError('');
    try {
      const updated = await customerApi.resendInvitation(invitation.id);
      setInvitations(current => current.map(item => (item.id === updated.id ? { ...item, ...updated } : item)));
      if (updated.registrationUrl) {
        setRegistrationUrls(current => ({ ...current, [updated.id]: updated.registrationUrl! }));
      }
    } catch {
      setActionError(t('customerInvitations.resendError'));
    } finally {
      setResendingId(null);
    }
  };

  const handleCopyLink = (id: string) => {
    const url = registrationUrls[id];
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const formatDate = (value?: string) => value
    ? new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-GB' : 'sv-SE', { dateStyle: 'short' }).format(new Date(value))
    : '';

  const statusBadge = (invitation: InvitationDetails, className = '') => (
    <span className={`badge ${STATUS_BADGE[invitation.status] ?? 'bg-secondary'} ${className}`.trim()}>
      {t(`customerInvitations.status.${invitation.status}`)}
    </span>
  );

  // Delivery, resend and the new link, under the status in the table and on the card.
  const deliveryActions = (invitation: InvitationDetails) => (
    <div className="mt-1">
      <div className="small text-muted">{t(`customerInvitations.emailStatus.${invitation.emailStatus || 'not_sent'}`)}</div>
      {isActiveInvitation(invitation.status) && (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary mt-2 customer-invitation-action"
          disabled={resendingId === invitation.id}
          onClick={(event) => { event.stopPropagation(); void handleResend(invitation); }}
        >
          <i className="feather-send me-1" aria-hidden="true"></i>
          {resendingId === invitation.id ? t('customerInvitations.resending') : t('customerInvitations.resend')}
        </button>
      )}
      {registrationUrls[invitation.id] && (
        <button
          type="button"
          className="btn btn-sm btn-link mt-2 customer-invitation-action"
          onClick={(event) => { event.stopPropagation(); handleCopyLink(invitation.id); }}
        >
          {copiedId === invitation.id ? t('customerInvitations.linkCopied') : t('customerInvitations.copyNewLink')}
        </button>
      )}
    </div>
  );

  const statusOptions = useMemo(
    () => FILTER_STATUSES.map(value => ({ value, label: t(`customerInvitations.status.${value}`) })),
    [t]
  );

  // Memoized: the table re-reports its rows whenever the columns change.
  const columns: ProjectsListColumn<InvitationDetails>[] = useMemo(() => [
    { field: 'customerName', label: t('customerInvitations.columns.customerName'), sortValue: nameOf, filterValue: nameOf, render: i => nameOf(i) || '–' },
    { field: 'customerEmail', label: t('customerInvitations.columns.customerEmail'), sortValue: emailOf, filterValue: emailOf, render: i => <span className="text-break">{emailOf(i)}</span> },
    { field: 'customerPhone', label: t('customerInvitations.columns.customerPhone'), sortValue: phoneOf, filterValue: phoneOf, render: i => phoneOf(i) || '–' },
    {
      field: 'status',
      label: t('customerInvitations.columns.status'),
      filter: 'select',
      selectAllLabel: t('customers.allStatuses'),
      selectOptions: statusOptions,
      sortValue: i => t(`customerInvitations.status.${i.status}`),
      render: i => <>{statusBadge(i)}{deliveryActions(i)}</>
    },
    {
      field: 'createdAt',
      label: t('customerInvitations.columns.createdAt'),
      defaultSortDirection: 'desc',
      sortValue: i => (i.createdAt ? Date.parse(i.createdAt) : 0),
      filterValue: i => formatDate(i.createdAt),
      render: i => <span className="text-nowrap">{formatDate(i.createdAt)}</span>
    }
    // The renderers depend on the language and the resend/copy state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [statusOptions, t, resendingId, copiedId, registrationUrls]);

  const renderMobileCard = (invitation: InvitationDetails) => (
    <div className="card mb-2">
      <div className="card-body py-2 px-3">
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className="fw-semibold text-truncate">{nameOf(invitation) || '–'}</span>
          {statusBadge(invitation, 'flex-shrink-0')}
        </div>
        <div className="text-muted small text-truncate"><i className="feather-mail me-1"></i>{emailOf(invitation)}</div>
        {phoneOf(invitation) && <div className="text-muted small"><i className="feather-phone me-1"></i>{phoneOf(invitation)}</div>}
        <div className="text-muted small mt-1"><i className="feather-calendar me-1"></i>{formatDate(invitation.createdAt)}</div>
        {deliveryActions(invitation)}
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('customerInvitations.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('customerInvitations.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('customerInvitations.description')}</p>
        </div>
      </div>

      {actionError && <div className="alert alert-danger" role="alert">{actionError}</div>}
      {loadFailed && <div className="alert alert-danger" role="alert">{t('customerInvitations.loadError')}</div>}
      <Card>
        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        ) : (
          <ProjectsListTable
            rows={invitations}
            columns={columns}
            rowKey={i => i.id}
            columnOrder={columnOrder}
            onColumnReorder={handleColumnReorder}
            reorderTitle={t('customers.dragToReorder')}
            initialSortField="createdAt"
            initialSortDirection="desc"
            actionTitle=""
            emptyText={t('customerInvitations.emptyList')}
            noResultsText={t('customerInvitations.noResults')}
            rangeText={range => t('customerInvitations.showingRange', range)}
            mobileFilter={{
              searchField: 'customerName',
              select: { field: 'status', allLabel: t('customers.allStatuses'), options: statusOptions },
              sort: {
                label: t('projects.sort.label'),
                options: [
                  { field: 'createdAt', direction: 'desc', label: t('customerInvitations.sort.newest') },
                  { field: 'createdAt', direction: 'asc', label: t('customerInvitations.sort.oldest') },
                  { field: 'customerName', direction: 'asc', label: t('customers.sort.nameAZ') }
                ]
              }
            }}
            renderMobileCard={renderMobileCard}
            testIds={{ table: 'customer-invitations-table', cards: 'customer-invitations-cards' }}
            toolbar={
              <Button className="admin-list-toolbar__btn" onClick={() => navigate('/customers/invite')} aria-label={t('customers.inviteCustomer')}>
                <i className="feather-user-plus" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('customers.inviteCustomer')}</span>
              </Button>
            }
          />
        )}
      </Card>
    </DashboardLayout>
  );
}
